import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Empty, Skeleton, Space, Tag, Typography } from "antd";
import Axios from "axios";
import { endpoints } from "../../auth/apiRoute";
import { getEventImageUrl } from "../../eventImages";

const { Title, Text, Paragraph } = Typography;

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

const statusMeta = (event) => {
  if (event.status === "on-sale") {
    return {
      className: "eventsList-statusOnSale",
      label: "Tickets available",
      action: "Buy tickets",
    };
  }
  if (event.status === "closed") {
    return {
      className: "eventsList-statusClosed",
      label: "Sales closed",
      action: "Closed",
    };
  }
  if (event.startDate) {
    return {
      className: "eventsList-statusSoon",
      label: `Opens ${formatDate(event.startDate)}`,
      action: "Coming soon",
    };
  }
  return {
    className: "eventsList-statusSoon",
    label: "Coming soon",
    action: "Coming soon",
  };
};

export default function EventsList() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    Axios.get(endpoints.events.list)
      .then((response) => {
        setEvents(response.data);
        setError(false);
      })
      .catch((error) => {
        console.log(error);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="eventsList-page">
        <div className="eventsList-shell">
          <Skeleton active paragraph={{ rows: 1 }} />
          <div className="eventsList-grid eventsList-loadingGrid">
            <Skeleton.Image active className="eventsList-skeletonImage" />
            <Skeleton.Image active className="eventsList-skeletonImage" />
            <Skeleton.Image active className="eventsList-skeletonImage" />
          </div>
        </div>
      </div>
    );
  }

  const onSaleEvents = events.filter((event) => event.status === "on-sale");
  const unavailableEvents = events.filter((event) => event.status !== "on-sale");
  const renderEventCard = (event, variant = "") => {
    const status = statusMeta(event);
    const eventDate = formatDate(event.startDate);
    const content = (
      <article
        className={`eventsList-card ${
          event.status === "on-sale" ? "eventsList-cardActive" : ""
        } ${variant}`}
      >
        <div className="eventsList-imageWrap">
          <img src={getEventImageUrl(event)} alt={event.name} />
        </div>
        <div className="eventsList-cardBody">
          <div className="eventsList-cardHeader">
            <Tag className={status.className}>{status.label}</Tag>
            {eventDate ? (
              <span className="eventsList-date">{eventDate}</span>
            ) : null}
          </div>
          <Title level={3}>{event.name}</Title>
          <Paragraph>
            {event.description ||
              "Event details and ticket information are managed by HANYC."}
          </Paragraph>
          <Button
            type={event.status === "on-sale" ? "primary" : "default"}
            disabled={event.status !== "on-sale"}
            block
          >
            {status.action}
          </Button>
        </div>
      </article>
    );

    return event.status === "on-sale" ? (
      <Link
        className="eventsList-link"
        to={`/event/${event.key}`}
        key={event.key}
      >
        {content}
      </Link>
    ) : (
      <div key={event.key}>{content}</div>
    );
  };

  return (
    <div className="eventsList-page">
      <div className="eventsList-shell">
        <section className="eventsList-hero">
          <Text className="eventsList-kicker">HANYC Events</Text>
          <Title level={1}>Upcoming events</Title>
          <Paragraph>
            Register for current HANYC events, view ticket availability, and
            check back for newly opened programs.
          </Paragraph>
        </section>

        {error ? (
          <div className="eventsList-empty">
            <Empty description="Events could not be loaded." />
          </div>
        ) : events.length === 0 ? (
          <div className="eventsList-empty">
            <Empty description="No public events are available right now." />
          </div>
        ) : (
          <Space direction="vertical" size={34} style={{ width: "100%" }}>
            {onSaleEvents.length > 0 ? (
              <section aria-label="Events on sale">
                <div className="eventsList-sectionHeader">
                  <Title level={2}>Available now</Title>
                  <Text>Events currently accepting registrations.</Text>
                </div>
                <div
                  className={
                    onSaleEvents.length === 1
                      ? "eventsList-featureGrid"
                      : "eventsList-masonryGrid"
                  }
                >
                  {onSaleEvents.map((event) =>
                    renderEventCard(
                      event,
                      onSaleEvents.length === 1 ? "eventsList-cardFeature" : ""
                    )
                  )}
                </div>
              </section>
            ) : (
              <div className="eventsList-empty">
                <Empty description="No events are currently on sale." />
              </div>
            )}

            {unavailableEvents.length > 0 ? (
              <section
                className="eventsList-secondarySection"
                aria-label="Unavailable events"
              >
                <div className="eventsList-sectionHeader">
                  <Title level={2}>Not currently available</Title>
                  <Text>
                    These events are coming soon or no longer accepting tickets.
                  </Text>
                </div>
                <div className="eventsList-masonryGrid eventsList-gridMuted">
                  {unavailableEvents.map((event) => renderEventCard(event))}
                </div>
              </section>
            ) : null}
          </Space>
        )}
      </div>
    </div>
  );
}
